import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSignOutAlt,
  faBars,
  faTimes,
  faSun,
  faMoon,
  faChevronDown,
  faChevronRight,
  faHome,
  faUser,
  faCog,
  faQuestionCircle,
  faShield,
  faFlask
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useBranding } from '../../contexts/BrandingContext';
import projectService from '../../services/projectService';
import NotificationBell from '../notifications/NotificationBell';
import { APP_VERSION } from '../../version';

const Layout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showTeamSelectModal, setShowTeamSelectModal] = useState(false);
  const [pendingProject, setPendingProject] = useState(null);
  const { darkMode, toggleTheme } = useTheme();
  const { logo, logoLight, appName, primaryColor, footerText } = useBranding();

  // Determine which teams the user can access
  const getUserTeamAccess = () => {
    if (!user) return [];
    if (user.role === 'admin' || user.role === 'tenant_admin' || user.role === 'purple_team') {
      return ['red', 'blue', 'purple'];
    }
    if (user.role === 'red_team') {
      return ['red'];
    }
    if (user.role === 'blue_team') {
      return ['blue'];
    }
    return [user.team || 'red'];
  };

  const userTeamAccess = getUserTeamAccess();

  // Get current project ID from URL
  const getCurrentProjectId = () => {
    const match = location.pathname.match(/\/projects\/([a-f0-9]+)/i);
    return match ? match[1] : null;
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Save selected project to localStorage when changed
  useEffect(() => {
    if (selectedProject) {
      localStorage.setItem('lastSelectedProject', selectedProject._id);
    }
  }, [selectedProject]);

  // Restore last selected project on load
  useEffect(() => {
    const projectId = getCurrentProjectId();
    if (projectId && projects.length > 0) {
      const project = projects.find(p => p._id === projectId);
      setSelectedProject(project || null);
    } else if (!projectId && projects.length > 0) {
      // No project in URL - try to restore from localStorage
      const lastProjectId = localStorage.getItem('lastSelectedProject');
      if (lastProjectId) {
        const project = projects.find(p => p._id === lastProjectId);
        if (project) {
          setSelectedProject(project);
          // Auto-navigate to the last project if we're on a generic page
          if (location.pathname === '/' || location.pathname === '/projects') {
            // Don't auto-navigate, just set the selected project in the dropdown
            // User can click on it to navigate
          }
        }
      }
    }
  }, [location.pathname, projects]);

  const fetchProjects = async () => {
    try {
      const data = await projectService.getProjects();
      setProjects(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
    navigate('/login');
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setProjectMenuOpen(false);

    // If user has access to multiple teams, show team selection modal
    if (userTeamAccess.length > 1) {
      setPendingProject(project);
      setShowTeamSelectModal(true);
    } else {
      // Single team access - navigate to that team's view
      const teamPath = getTeamPathForSingleTeam(project._id, userTeamAccess[0]);
      navigate(teamPath);
    }
  };

  // Get the appropriate path for a user with single team access
  const getTeamPathForSingleTeam = (projectId, team) => {
    switch (team) {
      case 'red':
        return `/projects/${projectId}`; // Red team goes to project overview (attack-focused)
      case 'blue':
        return `/projects/${projectId}/blue-team`;
      case 'purple':
        return `/projects/${projectId}/purple-team`;
      default:
        return `/projects/${projectId}`;
    }
  };

  // Handle team selection from modal
  const handleTeamSelect = (team) => {
    if (!pendingProject) return;
    const projectId = pendingProject._id;
    setShowTeamSelectModal(false);
    setPendingProject(null);

    const teamPath = getTeamPathForSingleTeam(projectId, team);
    navigate(teamPath);
  };

  const isAdmin = user?.role === 'admin';
  const isTenantAdmin = user?.role === 'tenant_admin';
  const isAnyAdmin = isAdmin || isTenantAdmin;
  const currentProjectId = getCurrentProjectId();

  // Build breadcrumbs based on current path
  const buildBreadcrumbs = () => {
    const crumbs = [];
    const pathParts = location.pathname.split('/').filter(Boolean);

    if (pathParts.length === 0 || pathParts[0] === '') {
      return [{ label: 'Dashboard', path: '/' }];
    }

    // Always start with home for admin, or project selector for others
    if (isAdmin) {
      crumbs.push({ label: 'Home', path: '/', icon: faHome });
    }

    if (pathParts[0] === 'projects') {
      crumbs.push({ label: 'Projects', path: '/projects' });

      if (pathParts[1] && selectedProject) {
        crumbs.push({ label: selectedProject.name, path: `/projects/${pathParts[1]}` });

        // Add sub-pages
        if (pathParts[2]) {
          const subPageLabels = {
            'targets': 'Targets',
            'campaigns': 'Campaigns',
            'attack-chain': 'Attack Chain',
            'debrief': 'Debrief',
            'reports': 'Reports',
            'edit': 'Edit',
            'actions': 'Actions',
            'blue-team': 'Blue Team',
            'purple-team': 'Purple Team'
          };
          if (subPageLabels[pathParts[2]]) {
            crumbs.push({ label: subPageLabels[pathParts[2]], path: location.pathname });
          }
        }
      }
    } else if (pathParts[0] === 'agents') {
      crumbs.push({ label: 'Agents', path: '/agents' });
      if (pathParts[1]) {
        crumbs.push({ label: 'Agent Details', path: location.pathname });
      }
    } else if (pathParts[0] === 'playbooks') {
      crumbs.push({ label: 'Playbooks', path: '/playbooks' });
      if (pathParts[1] === 'new') {
        crumbs.push({ label: 'New Playbook', path: location.pathname });
      } else if (pathParts[1]) {
        crumbs.push({ label: 'Edit Playbook', path: location.pathname });
      }
    } else if (pathParts[0] === 'atomic-tests') {
      crumbs.push({ label: 'Atomic Red Team Library', path: '/atomic-tests' });
    } else if (pathParts[0] === 'purplehq-tests') {
      crumbs.push({ label: 'PurpleHQ Custom Tests', path: '/purplehq-tests' });
    } else if (pathParts[0] === 'scheduler') {
      crumbs.push({ label: 'Assessment Scheduler', path: '/scheduler' });
    } else if (pathParts[0] === 'blue-team') {
      crumbs.push({ label: 'Blue Team', path: '/blue-team' });
    } else if (pathParts[0] === 'purple-team') {
      crumbs.push({ label: 'Purple Team', path: '/purple-team' });
    } else if (pathParts[0] === 'admin') {
      crumbs.push({ label: 'Admin', path: null });
      if (pathParts[1] === 'users') {
        crumbs.push({ label: 'Users', path: '/admin/users' });
      } else if (pathParts[1] === 'teams') {
        crumbs.push({ label: 'Teams', path: '/admin/teams' });
      }
    } else if (pathParts[0] === 'executive') {
      crumbs.push({ label: 'Executive Dashboard', path: '/executive' });
    } else if (pathParts[0] === 'reports') {
      crumbs.push({ label: 'Reports', path: '/reports' });
    }

    return crumbs;
  };

  // Main navigation structure with submenus
  // Team view paths are project-specific when a project is selected
  // Use selectedProject._id if available (from dropdown), otherwise fallback to currentProjectId (from URL), or localStorage
  const activeProjectId = selectedProject?._id || currentProjectId || localStorage.getItem('lastSelectedProject');

  const getTeamViewPath = (basePath) => {
    return activeProjectId ? `/projects/${activeProjectId}${basePath}` : basePath;
  };

  const mainNavItems = [
    {
      id: 'project',
      label: selectedProject ? selectedProject.name : 'Select Project',
      isProjectSelector: true,
    },
    {
      id: 'operations',
      label: 'Operations',
      submenu: [
        { path: '/projects', label: 'Projects' },
        { path: '/agents', label: 'Agents' },
        { path: '/c2', label: 'C2 Console', icon: faShield },
        { path: '/playbooks', label: 'Playbooks' },
        { path: '/atomic-tests', label: 'Atomic Tests' },
        { path: '/purplehq-tests', label: 'PurpleHQ Tests', icon: faFlask },
        { path: '/scheduler', label: 'Scheduler' },
      ]
    },
    {
      id: 'teams',
      label: 'Team Views',
      submenu: [
        { path: activeProjectId ? `/projects/${activeProjectId}` : '/projects', label: 'Red Team' },
        { path: getTeamViewPath('/blue-team'), label: 'Blue Team' },
        { path: getTeamViewPath('/purple-team'), label: 'Purple Team' },
      ]
    },
  ];

  // Admin-only items (super admin gets full admin menu, tenant admin gets tenant management)
  const adminNavItems = isAdmin ? [
    {
      id: 'admin',
      label: 'Admin',
      submenu: [
        { path: '/', label: 'Dashboard' },
        { path: '/executive', label: 'Executive' },
        { path: '/projects', label: 'Projects' },
        { path: '/admin/teams', label: 'Teams' },
        { path: '/admin/users', label: 'Users' },
        { path: '/super-admin/tenants', label: 'Tenants' },
      ]
    }
  ] : isTenantAdmin ? [
    {
      id: 'admin',
      label: 'Manage',
      submenu: [
        { path: '/', label: 'Dashboard' },
        { path: '/executive', label: 'Executive' },
        { path: '/tenant-settings', label: 'Tenant Settings' },
        { path: '/tenant-users', label: 'Users' },
        { path: '/admin/teams', label: 'Teams' },
      ]
    }
  ] : [];

  const allNavItems = [...mainNavItems, ...adminNavItems];

  const breadcrumbs = buildBreadcrumbs();

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-200 ${
      darkMode ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      {/* Top Navigation Bar */}
      <nav className={`sticky top-0 z-50 transition-colors duration-200 ${
        darkMode
          ? 'bg-gray-800 border-b border-gray-700'
          : 'bg-white border-b border-gray-200 shadow-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12">
            {/* Logo */}
            <Link to={isAdmin ? '/' : '/projects'} className="flex items-center space-x-2">
              {/* Use custom logo if available, otherwise fallback to default */}
              {(darkMode ? (logoLight || logo) : logo) ? (
                <img
                  src={darkMode ? (logoLight || logo) : logo}
                  alt={appName}
                  className="h-8 w-auto max-w-[150px] object-contain"
                />
              ) : (
                <>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    <span className="w-2 h-2 bg-blue-500 rounded-full -ml-0.5"></span>
                  </div>
                  <span className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <span style={{ color: primaryColor }}>{appName}</span>
                  </span>
                </>
              )}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {allNavItems.map((item) => (
                <div key={item.id} className="relative">
                  {item.isProjectSelector ? (
                    // Project Selector Dropdown
                    <div className="relative">
                      <button
                        onClick={() => {
                          setProjectMenuOpen(!projectMenuOpen);
                          setActiveSubmenu(null);
                        }}
                        className={`flex items-center space-x-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                          currentProjectId
                            ? 'text-white'
                            : darkMode
                              ? 'text-gray-300 hover:bg-gray-700'
                              : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        style={currentProjectId ? { backgroundColor: primaryColor } : {}}
                      >
                        <span className="max-w-28 truncate">{item.label}</span>
                        <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
                      </button>

                      {projectMenuOpen && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setProjectMenuOpen(false)}></div>
                          <div className={`absolute left-0 mt-1 w-56 max-h-72 overflow-y-auto rounded shadow-lg z-20 ${
                            darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                          }`}>
                            {projects.length > 0 ? (
                              projects.map((project) => (
                                <button
                                  key={project._id}
                                  onClick={() => handleProjectSelect(project)}
                                  className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${
                                    selectedProject?._id === project._id
                                      ? ''
                                      : darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                                  }`}
                                  style={selectedProject?._id === project._id ? {
                                    backgroundColor: darkMode ? `${primaryColor}30` : `${primaryColor}15`,
                                    color: darkMode ? '#ffffff' : primaryColor
                                  } : {}}
                                >
                                  <span className="truncate">{project.name}</span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    project.status === 'active' ? 'bg-green-500/20 text-green-500' :
                                    project.status === 'completed' ? 'bg-blue-500/20 text-blue-500' :
                                    'bg-gray-500/20 text-gray-500'
                                  }`}>
                                    {project.status}
                                  </span>
                                </button>
                              ))
                            ) : (
                              <div className={`px-3 py-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                No projects available
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    // Regular dropdown menu
                    <div className="relative">
                      <button
                        onClick={() => {
                          setActiveSubmenu(activeSubmenu === item.id ? null : item.id);
                          setProjectMenuOpen(false);
                        }}
                        className={`flex items-center space-x-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                          darkMode
                            ? 'text-gray-300 hover:bg-gray-700'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <span>{item.label}</span>
                        <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
                      </button>

                      {activeSubmenu === item.id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setActiveSubmenu(null)}></div>
                          <div className={`absolute left-0 mt-1 w-44 rounded shadow-lg z-20 ${
                            darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                          }`}>
                            {item.submenu.map((subItem) => (
                              <Link
                                key={subItem.path}
                                to={subItem.path}
                                onClick={() => setActiveSubmenu(null)}
                                className={`block px-3 py-2 text-sm ${
                                  location.pathname === subItem.path || location.pathname.startsWith(subItem.path + '/')
                                    ? darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'
                                    : darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {subItem.icon && <FontAwesomeIcon icon={subItem.icon} className="mr-2" />}
                                {subItem.label}
                              </Link>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Right side controls */}
            <div className="flex items-center space-x-2">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-1.5 rounded transition-colors ${
                  darkMode ? 'text-yellow-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                <FontAwesomeIcon icon={darkMode ? faSun : faMoon} />
              </button>

              {/* Notification Bell */}
              {user && <NotificationBell />}

              {/* Help/Documentation */}
              {user && (
                <Link
                  to="/help"
                  className={`p-2 rounded-full transition-colors ${
                    darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-600'
                  }`}
                  title="Help & Documentation"
                >
                  <FontAwesomeIcon icon={faQuestionCircle} />
                </Link>
              )}

              {/* Settings (Admin and Tenant Admin) */}
              {user && isAnyAdmin && (
                <Link
                  to={isTenantAdmin ? '/tenant-settings' : '/settings'}
                  className={`p-2 rounded-full transition-colors ${
                    darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-600'
                  }`}
                  title={isTenantAdmin ? 'Tenant Settings' : 'Settings'}
                >
                  <FontAwesomeIcon icon={faCog} />
                </Link>
              )}

              {/* User Menu */}
              <div className="relative">
                {user ? (
                  <>
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className={`flex items-center space-x-2 px-2 py-1 rounded transition-colors ${
                        darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span
                        className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                          user.role === 'admin' ? 'bg-yellow-500/20 text-yellow-500' :
                          user.role === 'tenant_admin' ? 'bg-orange-500/20 text-orange-400' :
                          user.role === 'blue_team' ? 'bg-blue-500/20 text-blue-500' :
                          user.role === 'red_team' ? 'bg-red-500/20 text-red-500' :
                          ''
                        }`}
                        style={user.role === 'purple_team' ? {
                          backgroundColor: `${primaryColor}20`,
                          color: primaryColor
                        } : {}}
                      >
                        {user.role?.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className="text-sm hidden sm:inline">{user.displayName || user.username}</span>
                      <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
                    </button>

                    {userMenuOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)}></div>
                        <div className={`absolute right-0 mt-1 w-40 rounded shadow-lg z-20 ${
                          darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                        }`}>
                          <Link
                            to="/profile"
                            onClick={() => setUserMenuOpen(false)}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center ${
                              darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <FontAwesomeIcon icon={faUser} className="mr-2" />
                            Profile
                          </Link>
                          <button
                            onClick={() => { handleLogout(); setUserMenuOpen(false); }}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center ${
                              darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
                            Sign out
                          </button>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="px-3 py-1.5 rounded text-sm font-medium text-white hover:opacity-90"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Sign In
                  </Link>
                )}
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`lg:hidden p-1.5 rounded ${
                  darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                <FontAwesomeIcon icon={mobileMenuOpen ? faTimes : faBars} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className={`lg:hidden border-t ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
            <div className="px-4 py-3 space-y-3">
              {/* Project Selector Mobile */}
              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Project
                </label>
                <select
                  value={selectedProject?._id || ''}
                  onChange={(e) => {
                    const project = projects.find(p => p._id === e.target.value);
                    if (project) handleProjectSelect(project);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full px-3 py-2 rounded text-sm ${
                    darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'
                  } border`}
                >
                  <option value="">Select Project</option>
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>{project.name}</option>
                  ))}
                </select>
              </div>

              {/* Mobile Nav Links */}
              {allNavItems.filter(item => !item.isProjectSelector).map((item) => (
                <div key={item.id}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {item.label}
                  </p>
                  <div className="space-y-1">
                    {item.submenu.map((subItem) => (
                      <Link
                        key={subItem.path}
                        to={subItem.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block px-3 py-2 rounded text-sm ${
                          location.pathname === subItem.path
                            ? darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'
                            : darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {subItem.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}

              {/* Mobile Profile & Sign Out */}
              {user && (
                <>
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`w-full text-left px-3 py-2 rounded text-sm flex items-center ${
                      darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <FontAwesomeIcon icon={faUser} className="mr-2" />
                    Profile
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className={`w-full text-left px-3 py-2 rounded text-sm ${
                      darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
                    Sign out
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Breadcrumbs + Project Sub-navigation */}
      {(breadcrumbs.length > 0 || currentProjectId) && (
        <div className={`border-b ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumbs */}
            <div className="flex items-center py-2 text-sm">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className={`mx-2 text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}
                    />
                  )}
                  {crumb.path && index < breadcrumbs.length - 1 ? (
                    <Link
                      to={crumb.path}
                      className={`hover:underline ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      {crumb.icon && <FontAwesomeIcon icon={crumb.icon} className="mr-1" />}
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className={darkMode ? 'text-gray-200' : 'text-gray-900'}>
                      {crumb.icon && <FontAwesomeIcon icon={crumb.icon} className="mr-1" />}
                      {crumb.label}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Project Sub-navigation (only when viewing a project) */}
            {currentProjectId && (
              <div className="flex items-center space-x-1 pb-2 overflow-x-auto">
                {[
                  { path: `/projects/${currentProjectId}`, label: 'Overview', color: 'primary' },
                  { path: `/projects/${currentProjectId}/targets`, label: 'Targets', color: 'primary' },
                  { path: `/projects/${currentProjectId}/campaigns`, label: 'Campaigns', color: 'primary' },
                  { path: `/projects/${currentProjectId}/actions`, label: 'Actions', color: 'primary' },
                  { path: `/projects/${currentProjectId}/attack-chain`, label: 'Attack Chain', color: 'primary' },
                  { path: `/projects/${currentProjectId}/c2`, label: 'C2 Console', color: 'primary' },
                  { path: `/projects/${currentProjectId}/web-testing`, label: 'Web Testing', color: 'teal' },
                  { path: `/projects/${currentProjectId}/import-scan`, label: 'Import Scan', color: 'primary' },
                  { path: `/projects/${currentProjectId}/blue-team`, label: 'Blue Team', color: 'blue' },
                  { path: `/projects/${currentProjectId}/purple-team`, label: 'Purple Team', color: 'primary' },
                  { path: `/projects/${currentProjectId}/debrief`, label: 'Debrief', color: 'primary' },
                  { path: `/projects/${currentProjectId}/reports`, label: 'Reports', color: 'primary' },
                ].map((item) => {
                  const isActive = location.pathname === item.path ||
                    (item.path.includes('/targets') && location.pathname.includes('/targets')) ||
                    (item.path.includes('/campaigns') && location.pathname.includes('/campaigns')) ||
                    (item.path.includes('/actions') && location.pathname.includes('/actions')) ||
                    (item.path.includes('/c2') && location.pathname.includes('/c2')) ||
                    (item.path.includes('/web-testing') && location.pathname.includes('/web-testing')) ||
                    (item.path.includes('/import-scan') && location.pathname.includes('/import-scan')) ||
                    (item.path.includes('/blue-team') && location.pathname.includes('/blue-team')) ||
                    (item.path.includes('/purple-team') && location.pathname.includes('/purple-team'));

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`px-3 py-1 rounded text-sm whitespace-nowrap transition-colors ${
                        isActive
                          ? 'text-white'
                          : darkMode
                            ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                      }`}
                      style={isActive ? {
                        backgroundColor: item.color === 'blue' ? '#2563eb' : item.color === 'teal' ? '#0d9488' : primaryColor
                      } : {}}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>

      {/* Minimal Footer */}
      <footer className={`mt-auto py-3 ${
        darkMode ? 'bg-gray-800 border-t border-gray-700' : 'bg-white border-t border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {footerText || `${appName}. Purple Team Operations by Zerberos`}
              <span className={`ml-2 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>v{APP_VERSION}</span>
            </p>
            <div className="flex items-center space-x-1 text-xs">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
              <span className={darkMode ? 'text-gray-600' : 'text-gray-300'}>+</span>
              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
              <span className={darkMode ? 'text-gray-600' : 'text-gray-300'}>=</span>
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: primaryColor }}></span>
            </div>
          </div>
        </div>
      </footer>

      {/* Team Selection Modal */}
      {showTeamSelectModal && pendingProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 max-w-md w-full mx-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Select Team View
            </h2>
            <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Project: <strong>{pendingProject.name}</strong>
            </p>
            <div className="space-y-2">
              {userTeamAccess.includes('red') && (
                <button
                  onClick={() => handleTeamSelect('red')}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors hover:bg-red-50 dark:hover:bg-red-900/20 border-red-200 dark:border-red-800"
                >
                  <div className="flex items-center">
                    <span className="w-3 h-3 bg-red-500 rounded-full mr-3"></span>
                    <span className={darkMode ? 'text-white' : 'text-gray-900'}>Red Team</span>
                  </div>
                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Attack Operations</span>
                </button>
              )}
              {userTeamAccess.includes('blue') && (
                <button
                  onClick={() => handleTeamSelect('blue')}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                >
                  <div className="flex items-center">
                    <span className="w-3 h-3 bg-blue-500 rounded-full mr-3"></span>
                    <span className={darkMode ? 'text-white' : 'text-gray-900'}>Blue Team</span>
                  </div>
                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Defense & Detection</span>
                </button>
              )}
              {userTeamAccess.includes('purple') && (
                <button
                  onClick={() => handleTeamSelect('purple')}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors"
                  style={{
                    borderColor: `${primaryColor}40`,
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${primaryColor}10`}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div className="flex items-center">
                    <span className="w-3 h-3 rounded-full mr-3" style={{ backgroundColor: primaryColor }}></span>
                    <span className={darkMode ? 'text-white' : 'text-gray-900'}>Purple Team</span>
                  </div>
                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Collaboration View</span>
                </button>
              )}
            </div>
            <button
              onClick={() => { setShowTeamSelectModal(false); setPendingProject(null); }}
              className={`mt-4 w-full px-4 py-2 rounded text-sm ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
